import * as React from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { envIsCanada, envIsMexico, envIsNormal, envIsSweden } from '@/constants/constants';
import { useIntl } from 'react-intl';
import { isRegulatedLocale } from '@/shared/utility';
import { useLocation } from 'react-use';
import classNames from 'classnames';
import {
  HomeHeroAuthenticationStep,
  useHomeHeroAuthenticationStepData,
} from '@/store/authentication/AuthenticationStore';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { sendEventToAppWrapper } from '@/utils/app-wrapper-utils';
import { useLogin } from '@/hooks/useLogin';
import { useRegistration } from '@/hooks/useRegistration';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { UserPlusIcon } from '@heroicons/react/24/outline';

import useIsHomePage from '@/hooks/useIsHomePage';

const AuthButton = ({ isScrolled }: { isScrolled: boolean }) => {
  const locale = useSelector(selectLocale);
  const intl = useIntl();
  const { openModal } = useModal();
  const { pathname } = useLocation();
  const { openLogin } = useLogin();
  const { openRegistration, isRegistrationRemoved } = useRegistration();
  const { homeHeroAuthenticationStep } = useHomeHeroAuthenticationStepData();
  const isHomeRoute = useIsHomePage();

  const handleOpenRegistration = () => {
    triggerGTag(GTagEvents.header_register_click);
    if (isRegulatedLocale(locale)) {
      openModal(MODALS.PAYNPLAY_IFRAME, { amount: 0, login: true });
      return;
    }

    openRegistration({ openedFrom: OpenedFrom.Header });
  };

  const handleLoginClick = () => {
    triggerGTag(GTagEvents.header_login_click);
    sendEventToAppWrapper(GTagEvents.header_login_click);
    openLogin({ openedFrom: OpenedFrom.Header });
  };

  const isRegButtonHidden = useMemo(() => {
    // Registration is removed in restricted mode — never show the register button.
    if (isRegistrationRemoved) return true;

    // For normal and mexico builds Reg button should be hidden
    // only when showing Registration Form on homepage and
    // when not scrolled down
    if (envIsMexico() || envIsNormal())
      return (
        homeHeroAuthenticationStep === HomeHeroAuthenticationStep.Registration &&
        isHomeRoute &&
        !isScrolled
      );

    // For Canada build we hide register button only when on /register route
    if (envIsCanada()) return pathname?.toLocaleLowerCase().includes('register');

    return false;
  }, [homeHeroAuthenticationStep, isHomeRoute, isScrolled, pathname, isRegistrationRemoved]);

  const isLoginButtonHidden = useMemo(() => {
    // Login button is hidden only on homepage when showing Login Form
    // and when not scrolled down
    return (
      homeHeroAuthenticationStep === HomeHeroAuthenticationStep.Login && !isScrolled && isHomeRoute
    );
  }, [homeHeroAuthenticationStep, isScrolled, isHomeRoute]);

  if (isRegulatedLocale(locale)) {
    return (
      <button
        data-cy="authentication-button-registration"
        type="button"
        className={classNames(
          'rounded-full px-3 lg:px-4 font-bold transition hover:shadow-md py-2 text-white bg-blue-light hover:bg-cyan-600',
        )}
        onClick={handleOpenRegistration}
      >
        <p className="m-0 p-0">
          {intl.formatMessage({
            id: isRegistrationRemoved
              ? 'header.login'
              : envIsSweden()
              ? 'header.register.zeroDeposit'
              : 'header.register',
          })}
        </p>
      </button>
    );
  }

  return (
    <div className="flex gap-x-3 min-h-[40px]">
      <button
        type="button"
        data-cy="authentication-button-login"
        className={classNames(
          'h-10 rounded-full px-4 font-bold transition hover:shadow-md bg-white text-slate-900 hover:bg-white/90 leading-[14px]',
          'backdrop-blur-md border-blue-light border-[1px]',
          {
            hidden: isLoginButtonHidden,
          },
        )}
        onClick={handleLoginClick}
      >
        {intl.formatMessage({ id: 'header.login' })}
      </button>
      <button
        data-cy="authentication-button-registration"
        type="button"
        className={classNames(
          'rounded-full px-3 lg:px-4 font-bold transition hover:shadow-md text-white py-2 bg-blue-light hover:bg-cyan-600',
          'backdrop-blur-md border-white/20 border-t-[1px]',
          {
            hidden: isRegButtonHidden,
          },
        )}
        onClick={handleOpenRegistration}
      >
        <p className="m-0 p-0 hidden lg:block">{intl.formatMessage({ id: 'header.register' })}</p>
        <UserPlusIcon width={20} height={20} className="lg:hidden justify-self-center" />
      </button>
    </div>
  );
};

export default AuthButton;
